/* global Vue, VueI18n, LOYALTY_API, LOYALTY_PREFERENCES, LOYALTY_INTERESTS */

import { setSuccessAndStopLoading, setErrorAndStopLoading, startLoading, stopLoading } from '../../apps/components/Notifications'
import InterestGroup from '../../apps/components/InterestGroup'
import { AuthService, fetchWithTokenAsync } from '../../AuthService'

export default {
  name: 'Preferences',
  components: { InterestGroup },
  template: `
  <div class="loyalty-my-interests">
    <div class="container grid">
      <p class="large-text col-12 col-lg-10 offset-lg-1 col-xxl-8 offset-xxl-2">{{ $t('preferencesTab.leadText') }}</p>
    </div>

    <div class="container grid no-padding-sm-down">
      <div class="col-12 col-lg-10 offset-lg-1 col-xxl-8 offset-xxl-2">
        <div class="loyalty-my-interests__masonry">
          <InterestGroup
            v-for="type in types"
            :title="$t('preferencesTab.' + type)"
            :items="LOYALTY_PREFERENCES.filter(i => i.type === type)"
            :selectedItems="selectedItemsObj[type]"
            :allergiesBool="type === 'food' ? allergiesBool : false"
            :allergiesText="type === 'food' ? allergiesText : ''"
            @updateItems="$event => selectedItemsObj[type] = $event"
            @updateAllergiesText="$event => allergiesText = $event">

            <template v-slot:header v-if="type === 'rooms' || type === 'services'">
              <p class="small-text" v-text="$t('preferencesTab.' + type + 'Extra')"></p>
            </template>

            <template v-slot:footer v-if="type === 'bedding'">
              <p class="no-margin-bottom small-text" v-text="$t('preferencesTab.notAvailableInCampsites')"></p>
            </template>

            <template v-slot:footer v-if="type === 'childEquipment'">
              <p class="no-margin-bottom small-text" v-text="$t('preferencesTab.pickupAtReception')"></p>
            </template>
          </InterestGroup>
        </div>
      </div>
    </div>

    <div class="container grid">
      <div class="col-12 col-lg-10 offset-lg-1 col-xxl-8 offset-xxl-2 button-group">
        <button class="button button--outline" @click="$router.push('/')">{{ $t('cancel') }}</button>
        <button class="button button--maistar" @click="setPreferences" :disabled="!isValid">{{ $t('confirm') }}</button>
      </div>
    </div>
  </div>
  `,
  props: {
    loyaltyId: Number,
  },
  setup (props, { emit }) {
    const allergiesText = Vue.ref('')
    const { loyaltyId } = Vue.toRefs(props)
    const { t } = VueI18n.useI18n()
    const types = ['bedding', 'food', 'rooms', 'childEquipment', 'services']
    const interestTypes = ['holiday', 'brand', 'destination', 'campsite']

    const selectedItemsObj = Vue.reactive(
      types.reduce((obj, type) => {
        obj[type] = []
        return obj
      }, {}),
    )

    const selectedItemsArr = Vue.computed(() => Object.values(selectedItemsObj).flat())

    const allergiesBool = Vue.computed(() => selectedItemsArr.value.includes('Allergies'))

    const isValid = Vue.computed(() => allergiesBool.value ? allergiesText.value.length > 0 : true)

    const setPreferences = async () => {
      try {
        startLoading('setPreferences')
        const response = await fetchWithTokenAsync(`${LOYALTY_API}/api/v1/contacts/setInterests2`, AuthService.accessToken, 'PUT', {
          interests: selectedItemsArr.value.filter(p => p !== 'Allergies'),
          allergies: allergiesBool.value,
          allergiesText: allergiesText.value,
        })
        if (response.status !== 0) {
          throw new Error('Set member preferences failed.')
        }
        setSuccessAndStopLoading({ title: t('preferencesTab.updateSuccess') })
      } catch (e) {
        setErrorAndStopLoading(e)
      }
    }

    Vue.watch(loyaltyId, async () => {
      try {
        startLoading('preferences')
        const response = await fetchWithTokenAsync(`${LOYALTY_API}/api/v1/contacts/interests2`, AuthService.accessToken, 'GET')
        if (!response?.interests) {
          throw Error('Get interests failed')
        }

        [...types, ...interestTypes].forEach((type) => {
          selectedItemsObj[type] = [...LOYALTY_PREFERENCES, ...LOYALTY_INTERESTS].filter(i => response.interests.includes(i.key) && i.type === type).map(i => i.key)
        })
        if (response.allergies) {
          selectedItemsObj.food.push('Allergies')
        }
        allergiesText.value = response.allergiesText

        stopLoading('preferences')
      } catch (e) {
        console.error(e)
        emit('error', e)
      }
    }, { immediate: true })

    return {
      LOYALTY_PREFERENCES,
      selectedItemsObj,
      allergiesBool,
      allergiesText,
      isValid,
      types,
      setPreferences,
    }
  },
}
